import React, { useState } from 'react';
import { Box, Button, InputBase, Typography } from '@mui/material';
import { useUntypedTranslation } from '../../../hooks/useUntypedTranslation';
import {
  eyebrow,
  focusRing,
  keyQuestionCard,
  listItemText,
  meta,
  mono,
  pillAction,
  pressable,
} from '../../../styles/visualLanguage';
import { AdvisoryEngagementShell } from './AdvisoryEngagementShell';
import { stepPosition } from '../advisoryStepPosition';
import { normalizeControlCharacters } from '../../../redux/slices/advisory/advisoryTextSanitize';
import type {
  AdvisoryStage,
  CounterpartyProfileRow,
} from '../../../redux/slices/advisory/advisoryTypes';

/**
 * AdvisoryCounterpartyProfilesScreen
 *
 * What the user watches while their competitors — or acquisition targets — are
 * being researched, and where they can rescue the ones that came back empty.
 *
 * Why this screen exists at all: the research fires the moment the list is
 * confirmed, takes minutes per company, and the ANALYSIS WAITS for it. Without
 * a screen the user sits on a silent wizard with no way to tell work from a
 * hang, and no way to act on a company the research could not find.
 *
 * The retry affordance is the point of the second half. A company too new, too
 * small or too private to be found by name is exactly where one URL — an about
 * page, an IR page, a filing — turns an empty result into a profile. That is
 * knowledge the user has and the research does not, so the field is offered
 * precisely on the rows that failed and nowhere else.
 *
 * `Continue` is deliberately available while failures remain. A profile that
 * cannot be found is a permanent gap the answering stage reports as missing
 * evidence, and blocking the engagement on it would strand the user — the
 * retry is an offer, not a toll.
 *
 * And when the retry itself is exhausted — the URL the user supplied found
 * nothing either — the company is REMOVED from the engagement server-side, and
 * this screen is where they are told. That notice is not decoration: the
 * confirmed list they curated is now shorter, and a list that shrinks with no
 * explanation is indistinguishable from a bug. It names each company, says it
 * will not be analysed, and says why.
 */
export interface AdvisoryCounterpartyProfilesScreenProps {
  stage: Extract<AdvisoryStage, { tag: 'researchingCounterparties' }>;
  /** True while a retry turn is in flight. */
  busy?: boolean;
  /** `url` is omitted when the user retries without supplying one. */
  onRetry: (company: string, url?: string) => void;
  /** Leaves the screen with the gaps unfilled — see the class docstring. */
  onContinue: () => void;
}

/** Rows the user can still do something about. */
function isFailed(row: CounterpartyProfileRow): boolean {
  return row.status !== null && row.status !== 'researching' && row.status !== 'ingested';
}

export function AdvisoryCounterpartyProfilesScreen({
  stage,
  busy = false,
  onRetry,
  onContinue,
}: AdvisoryCounterpartyProfilesScreenProps) {
  const { t } = useUntypedTranslation();

  // Per-company URL drafts. Keyed by company, so typing into one row's field
  // cannot disturb another's — the rows re-render together on every snapshot.
  const [urls, setUrls] = useState<Record<string, string>>({});

  const kindKey = stage.kind === 'acquisition target' ? 'target' : 'competitor';
  const position = stepPosition('goal', null);

  const done = stage.rows.filter(row => row.status === 'ingested').length;
  const failedRows = stage.rows.filter(isFailed);

  const handleRetry = (company: string) => {
    if (busy) {
      return;
    }
    const draft = normalizeControlCharacters(urls[company] ?? '').trim();
    onRetry(company, draft || undefined);
    // The draft is deliberately KEPT. It is the seed of the run that just
    // started, and the stored `sourceUrl` that would replace it only arrives
    // when the fact is re-read at the end of the stage — clearing it here made
    // the URL the user had just typed vanish the instant they submitted it,
    // with nothing anywhere saying it had been accepted.
  };

  return (
    <AdvisoryEngagementShell
      position={position?.position ?? '02'}
      total={position?.total ?? null}
      eyebrowKey={`advisory:counterpartyProfiles.${kindKey}.eyebrow`}
      titleKey={`advisory:counterpartyProfiles.${kindKey}.title`}
      hintKey={`advisory:counterpartyProfiles.${kindKey}.hint`}
      actions={
        // Only once nothing is in flight. Continuing mid-research would hand the
        // user to a stage that simply waits for the same jobs, which reads as a
        // second, unexplained hang.
        stage.pending ? null : (
          <Button
            variant="contained"
            disableRipple
            disabled={busy}
            onClick={onContinue}
            data-testid="advisory-profiles-continue"
            sx={[pillAction, pressable, focusRing]}
          >
            {t(
              failedRows.length > 0
                ? 'advisory:counterpartyProfiles.continueWithGaps'
                : 'advisory:counterpartyProfiles.continueAction'
            )}
          </Button>
        )
      }
    >
      <Typography
        data-testid="advisory-profiles-count"
        sx={[eyebrow, theme => ({ marginTop: theme.visualPrimitives.clHeaderGap })]}
      >
        {t(`advisory:counterpartyProfiles.${kindKey}.countLabel`)}
        {' · '}
        <Box component="span" sx={[mono]}>
          {done}
        </Box>
        {' / '}
        <Box component="span" sx={[mono]}>
          {stage.rows.length}
        </Box>
      </Typography>

      {stage.pending ? (
        <Typography
          data-testid="advisory-profiles-waiting"
          sx={[meta, theme => ({ marginTop: theme.visualPrimitives.rowGap })]}
        >
          {t('advisory:counterpartyProfiles.waitingBody')}
        </Typography>
      ) : null}

      {stage.dropped.length > 0 ? (
        <Box
          data-testid="advisory-profiles-dropped"
          sx={theme => ({
            marginTop: theme.visualPrimitives.rowGap,
            paddingLeft: theme.visualPrimitives.inputPaddingY,
            borderLeft: `2px solid ${theme.visualPrimitives.border}`,
          })}
        >
          <Typography sx={[meta]}>
            {t(`advisory:counterpartyProfiles.dropped.${kindKey}.body`, {
              count: stage.dropped.length,
            })}
          </Typography>
          {stage.dropped.map(drop => (
            <Typography
              key={drop.company}
              data-testid="advisory-profiles-dropped-row"
              sx={[meta, theme => ({ marginTop: theme.visualPrimitives.inputPaddingY })]}
            >
              <Box component="span" sx={[listItemText]}>
                {drop.company}
              </Box>
              {' · '}
              {t('advisory:counterpartyProfiles.dropped.reason')}
            </Typography>
          ))}
        </Box>
      ) : null}

      <Box
        data-testid="advisory-profiles-list"
        sx={theme => ({ marginTop: theme.visualPrimitives.kqListGap })}
      >
        {stage.rows.map(row => (
          <Box key={row.company} data-testid="advisory-profile-row" sx={[keyQuestionCard]}>
            <Typography sx={[listItemText]}>{row.company}</Typography>
            <Typography
              data-testid="advisory-profile-status"
              sx={[meta]}
              data-status={row.status ?? 'queued'}
            >
              {t(`advisory:counterpartyProfiles.status.${row.status ?? 'queued'}`)}
              {row.status === 'ingested' && row.fileName ? (
                <>
                  {' · '}
                  <Box component="span" sx={[mono]}>
                    {row.fileName}
                  </Box>
                </>
              ) : null}
            </Typography>

            {row.status === 'researching' && (urls[row.company] || row.sourceUrl) ? (
              // The seed, stated back. A retry's whole point is the page the
              // user supplied, so the row says what it is working from rather
              // than dropping that context the moment the job starts.
              <Typography
                data-testid="advisory-profile-seeded"
                sx={[meta, theme => ({ marginTop: theme.visualPrimitives.inputPaddingY })]}
              >
                {t('advisory:counterpartyProfiles.seededFrom')}{' '}
                <Box component="span" sx={[mono]}>
                  {urls[row.company] || row.sourceUrl}
                </Box>
              </Typography>
            ) : null}

            {isFailed(row) ? (
              <Box
                data-testid="advisory-profile-retry"
                sx={theme => ({ marginTop: theme.visualPrimitives.rowGap })}
              >
                <Typography sx={[meta]}>
                  {t(`advisory:counterpartyProfiles.retryHint.${kindKey}`)}
                </Typography>
                <InputBase
                  fullWidth
                  inputProps={{
                    'aria-label': t('advisory:counterpartyProfiles.urlLabel', {
                      company: row.company,
                    }),
                    'data-testid': 'advisory-profile-url',
                  }}
                  placeholder={t('advisory:counterpartyProfiles.urlPlaceholder')}
                  value={urls[row.company] ?? row.sourceUrl ?? ''}
                  disabled={busy}
                  onChange={event =>
                    setUrls(current => ({ ...current, [row.company]: event.target.value }))
                  }
                  onKeyDown={event => {
                    if (event.key === 'Enter') {
                      handleRetry(row.company);
                    }
                  }}
                  sx={theme => ({
                    fontSize: theme.visualPrimitives.inputSize,
                    lineHeight: theme.visualPrimitives.inputLineHeight,
                    marginTop: theme.visualPrimitives.inputGap,
                    color: theme.colors.inkPrimary,
                    borderBottom: `1px solid ${theme.visualPrimitives.border}`,
                    '& .MuiInputBase-input': { padding: theme.visualPrimitives.inputPaddingY },
                    '& .MuiInputBase-input::placeholder': {
                      color: theme.colors.inkTertiary,
                      opacity: 1,
                    },
                    '&:focus-within': { borderBottomColor: theme.colors.lavenderDeep },
                  })}
                />
                <Button
                  variant="text"
                  disableRipple
                  disabled={busy}
                  onClick={() => handleRetry(row.company)}
                  data-testid="advisory-profile-retry-action"
                  sx={[pressable, focusRing]}
                >
                  {t('advisory:counterpartyProfiles.retryAction')}
                </Button>
              </Box>
            ) : null}
          </Box>
        ))}
      </Box>
    </AdvisoryEngagementShell>
  );
}

export default AdvisoryCounterpartyProfilesScreen;
